/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import {
  ProductDetail,
  STOItem,
  STOPickItemState,
  STOProcessedItem,
} from 'commons/types';
import { ReducerKeys } from 'redux-stores/enums/reducerKey.enum';

const initialState: STOPickItemState = {
  assignedSTOItems: [],
  stoItemSKUList: [],
  stoItems: {},
  stoPickedItem: null,
  binRecommendationInfo: [
    {
      bin: '-',
      bin_type: '-',
      inventory_number: '-',
      quantity: 0,
    } as ProductDetail,
  ],
};

const stoPickItemSlice = createSlice({
  name: ReducerKeys.STOPickItem,
  initialState,
  reducers: {
    setAssignedSTOPickItem(
      state: STOPickItemState,
      action: { type: string; payload: STOItem[] },
    ) {
      state.assignedSTOItems = action.payload;
    },
    addAssignedSTOPickItem(
      state: STOPickItemState,
      action: { type: string; payload: STOItem },
    ) {
      state.assignedSTOItems.push(action.payload);
    },
    removeAssignedSTOPickItem(
      state: STOPickItemState,
      action: { type: string; payload: STOItem },
    ) {
      const updatedTasks = state.assignedSTOItems.filter(
        (task) =>
          task.sto_number !== action.payload.sto_number ||
          task.sku_code !== action.payload.sku_code,
      );
      state.assignedSTOItems = updatedTasks;
    },
    updateAssignedSTOPickItem(
      state: STOPickItemState,
      action: { type: string; payload: STOItem },
    ) {
      const updatedItems = state.assignedSTOItems.map((item) =>
        item.sto_number === action.payload.sto_number ? action.payload : item,
      );
      state.assignedSTOItems = updatedItems;
    },
    prepareSTOPickItems(
      state: STOPickItemState,
      action: { type: string; payload: STOItem[] | null | undefined },
    ) {
      const { payload } = action;
      if (payload && payload.length > 0) {
        state.assignedSTOItems = payload;
      }
      const { assignedSTOItems } = state;
      const stoItem: Record<string, STOItem> = {};
      const skuList: string[] = [];
      assignedSTOItems.forEach((item) => {
        stoItem[item.sku_code] = item;
        skuList.push(item.sku_code);
      });
      state.stoItems = stoItem;
      state.stoItemSKUList = skuList;
    },
    updateSTOPickSKUList(
      state: STOPickItemState,
      action: { type: string; payload: string[] },
    ) {
      state.stoItemSKUList = action.payload;
    },
    setSTOPickedItem(
      state: STOPickItemState,
      action: { type: string; payload: STOItem | null },
    ) {
      state.stoPickedItem = action.payload;
    },
    setSTOPickBinRecommendationInfo(
      state: STOPickItemState,
      action: { type: string; payload: ProductDetail[] },
    ) {
      state.binRecommendationInfo = action.payload;
    },
    setSTOItemPickActual(
      state: STOPickItemState,
      action: {
        type: string;
        payload: { isRemove: boolean; item: STOProcessedItem; itemIdx: number };
      },
    ) {
      const { isRemove, item, itemIdx } = action.payload;
      if (state.assignedSTOItems[itemIdx]) {
        if (isRemove) {
          // pop the pick actual value
          state.assignedSTOItems[itemIdx].pick_actual = state.assignedSTOItems[
            itemIdx
          ].pick_actual.filter(
            (actual) =>
              item.bin !== actual.bin ||
              item.inventory_number !== actual.inventory_number,
          );
        } else {
          // replace/add the value
          const isExist = state.assignedSTOItems[itemIdx].pick_actual.find(
            (actual) =>
              item.bin === actual.bin &&
              item.inventory_number === actual.inventory_number,
          );
          if (isExist) {
            state.assignedSTOItems[itemIdx].pick_actual =
              state.assignedSTOItems[itemIdx].pick_actual.map((actual) =>
                item.bin === actual.bin &&
                item.inventory_number === actual.inventory_number
                  ? item
                  : actual,
              );
          } else {
            state.assignedSTOItems[itemIdx].pick_actual.push(item);
          }
        }
      }
    },
  },
});

export const {
  setAssignedSTOPickItem,
  addAssignedSTOPickItem,
  updateAssignedSTOPickItem,
  removeAssignedSTOPickItem,
  prepareSTOPickItems,
  updateSTOPickSKUList,
  setSTOPickedItem,
  setSTOItemPickActual,
  setSTOPickBinRecommendationInfo,
} = stoPickItemSlice.actions;

export default stoPickItemSlice.reducer;
